import cn from 'classnames';
import { isEmpty } from 'lodash-es';
import React, { FC, useCallback, useState } from 'react';
import { Accept, FileWithPath, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl';

import { Icon } from '../Icon/Icon';

import styles from './UploadImageInput.module.scss';

type UploadImageInputProps = {
  image: FileWithPath;
  className?: string;
  title?: string | React.ReactNode;
  dropDescription?: string | React.ReactNode;
  accept?: Accept;
  hideImageDescription?: boolean;
  previewImage?: string;
  isDeletable?: boolean;
  onChange: (image: File) => void;
  onRemove?: () => void;
};

export const UploadImageInput: FC<UploadImageInputProps> = (props) => {
  const {
    image,
    className,
    title,
    dropDescription,
    accept,
    hideImageDescription,
    previewImage,
    isDeletable = true,
    onChange,
    onRemove,
  } = props;

  const { formatMessage } = useIntl();

  const [isPreviewDeleted, setIsPreviewDeleted] = useState(false);

  const bytesToMegaBytes = (bytes: number) =>
    `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      onChange(acceptedFiles[0]);
    },
    [onChange],
  );

  const handleRemove: React.MouseEventHandler<SVGSVGElement> = (event) => {
    event.stopPropagation();

    if (onRemove && previewImage) {
      setIsPreviewDeleted(true);
      onRemove();
    }

    onChange({} as File);
  };

  const { getInputProps, getRootProps, isFocused } = useDropzone({
    onDrop,
    accept,
  });

  const imageDescription = !hideImageDescription && (
    <div className={styles.imageDescription}>
      <Icon className={styles.docIcon} name="doc" size={24} />
      <p>{image?.path}</p>
      <p className={styles.imageSize}>{bytesToMegaBytes(image?.size)}</p>
    </div>
  );

  const hasImage = !isEmpty(image);

  const showDeleteButton =
    isDeletable && (hasImage || (!isPreviewDeleted && previewImage));
  const showPreviewImage = previewImage && !isPreviewDeleted && !hasImage;
  const showDropDescription = !hasImage && (!previewImage || isPreviewDeleted);

  return (
    <div {...getRootProps()} className={cn(className, styles.container)}>
      <input {...getInputProps()} />
      <p className="p2">{title}</p>
      <div
        className={cn(styles.dragDescription, isFocused && styles.isFocused)}
      >
        {showPreviewImage && (
          <img
            src={previewImage}
            alt={formatMessage({
              id: 'uploadImage.imagePreview',
              defaultMessage: 'Image Preview',
            })}
          />
        )}
        {hasImage && (
          <div className={styles.image}>
            {imageDescription}

            <img
              src={URL.createObjectURL(image)}
              alt={formatMessage({
                id: 'uploadImage.imagePreview',
                defaultMessage: 'Image Preview',
              })}
            />
          </div>
        )}
        {showDropDescription && dropDescription}
      </div>
      {showDeleteButton && (
        <Icon
          className={styles.removeIcon}
          name="delete"
          size={32}
          onClick={handleRemove}
        />
      )}
    </div>
  );
};
